<script setup lang="ts">
import type { AttendeeData } from '@/types/attendee';

const props = defineProps({
	isShow: {
		type: Boolean,
		default: false,
	},
	eventId: {
		type: Number as PropType<number | null>,
		default: null,
	},
});

const emit = defineEmits<{
	(e: 'onClose'): void;
}>();

const profileStore = useProfileStore();
const attendeeStore = useAttendeeStore();
const router = useRouter();

const currentAttendeePage = ref(1);
const attendees = ref<AttendeeData[]>([]);

async function handleFetchData($state: InfiniteLoadingEvent) {
	if (!props.eventId) {
		$state.complete();
		return;
	}

	try {
		await attendeeStore.retrieveAttendees(
			props.eventId,
			currentAttendeePage.value,
			PAGINATION_CONFIG.ITEM_PER_SMALL_PAGE,
		);

		const newAttendees = attendeeStore.attendee.attendeeData?.attendees || [];
		// Append newly fetched page to local list
		attendees.value = [...attendees.value, ...newAttendees];

		// Decide completion after appending
		const totalItems = attendeeStore.attendee.attendeeData?.paginationInfo.totalItems;
		const pageSize = PAGINATION_CONFIG.ITEM_PER_SMALL_PAGE;

		// If API provides totalItems, stop when we've reached it
		if (attendees.value.length >= Number(totalItems)) {
			$state.complete();
			return;
		}

		// If fewer than a full page returned, we've reached the end
		if (newAttendees.length < pageSize) {
			$state.complete();
			return;
		}

		// Otherwise, load next page
		$state.loaded();
		currentAttendeePage.value++;
	} catch (error) {
		$state.complete();
	}
}

function handleNavigation(id: number) {
	emit('onClose');
	if (id === profileStore.profile?.id) {
		const profileUrlLink = getRouteUrl({ name: ROUTE.PROFILE.NAME }, router);
		openLink(profileUrlLink);
	} else {
		const userUrlLink = getRouteUrl({ name: ROUTE.USER.NAME, params: { id } }, router);
		openLink(userUrlLink);
	}
}

function handleClose() {
	// First emit the close event to trigger the dialog close animation
	emit('onClose');
	
	// Use a small delay to ensure the dialog close animation completes
	setTimeout(() => {
		// Reset local state after dialog is no longer visible
		currentAttendeePage.value = 1;
		attendees.value = [];
	}, 300); // 300ms should be enough for the dialog close animation to complete
}

// Whenever dialog opens, reset local list and page to fetch fresh data
watch(
	() => props.isShow,
	(isOpen) => {
		if (isOpen) {
			currentAttendeePage.value = 1;
			attendees.value = [];
		}
	},
);
</script>

<template>
	<InfiniteDialog :is-show="props.isShow" @on-fetch-data="handleFetchData" @on-close="handleClose">
		<template #header>
			<span class="text-lg text-semibold">People attending</span>
		</template>

		<div class="attendee-container">
			<BaseButton
				v-for="(att, idx) in attendees"
				:key="`${idx}-${att.id}`"
				class="attendee"
				variant="link"
				color="gray"
				@click="handleNavigation(att.id)"
			>
				<div class="attendee-avatar">
					<AvatarImage :image-url="att.avatar" :alt="att.nickname" />
					<p class="attendee-nickname">{{ att.nickname }}</p>
				</div>
				<p v-if="att.quantity > 1" class="attendee-quantity">{{ att.quantity }}x</p>
			</BaseButton>
		</div>

		<template #spinner>
			<VSkeletonLoader
				v-for="index in 8"
				:key="index"
				class="mx-auto"
				max-width="352"
				height="64"
				type="list-item-avatar"
			/>
		</template>
	</InfiniteDialog>
</template>

<style lang="scss" scoped>
.attendee-container {
	width: 100%;
	max-height: rem(512);
	display: flex;
	flex-direction: column;

	.attendee {
		width: 100%;
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding: spacings-get(3) spacings-get(6) spacings-get(3) 0;
		color: colors-get(gray, 900);
		@include fonts-get(medium, 'text-sm');

		.attendee-avatar {
			display: flex;
			align-items: center;
			gap: spacings-get(3);
		}

		.attendee-nickname {
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			text-align: left;
			@include fonts-get(medium, 'text-sm');
		}

		.attendee-quantity {
			white-space: nowrap;
			text-align: right;
		}
	}

	:deep(.v-skeleton-loader__avatar) {
		width: spacings-get(10);
		height: spacings-get(10);
		min-width: spacings-get(10);
		min-height: spacings-get(10);
	}
}
</style>
